import React, { useState } from 'react';
import cx from 'classnames';
import { forTemplate } from 'app/common/lib/util/i18n';
import {
  useSelectCoverQuery,
  useUpdateCardCoverMutation,
} from '@trello/graphql/generated';
import {
  smallestPreviewBiggerThan,
  biggestPreview,
} from 'app/common/lib/util/preview';
import { Upload } from './Upload';
import { track } from '@trello/analytics';

import styles from './SelectAttachmentCover.less';

const format = forTemplate('card_cover_chooser');

interface SelectAttachmentCoverProps {
  cardId: string;
}

export const SelectAttachmentCover: React.FunctionComponent<SelectAttachmentCoverProps> = ({
  cardId,
}) => {
  const { data } = useSelectCoverQuery({
    variables: { cardId },
    fetchPolicy: 'cache-only',
  });

  const [showMore, setShowMore] = useState(false);

  const [updateCardCover] = useUpdateCardCoverMutation();
  const setCover = async (
    cover: {
      idAttachment?: string;
      idUploadedBackground?: string;
    } | null,
  ) => {
    await updateCardCover({
      variables: {
        cardId,
        cover,
      },
    });
  };

  if (!data?.card) {
    throw new Error(
      'Failed to load card details from cache for SelectAttachmentCover.',
    );
  }

  const card = data.card;
  const { cover, attachments } = card;
  const idAttachmentCover = cover?.idAttachment;

  const potentialAttachmentCovers = [];

  for (const attachment of attachments) {
    const preview =
      smallestPreviewBiggerThan(attachment.previews, 256) ||
      biggestPreview(attachment.previews);

    if (!preview || !preview.url) {
      continue;
    }

    potentialAttachmentCovers.unshift({
      id: attachment.id,
      edgeColor: attachment.edgeColor || 'transparent',
      thumbnailUrl: preview.url,
      selected: idAttachmentCover === attachment.id,
    });
  }

  const potentialAttachmentCoversToShow = showMore
    ? potentialAttachmentCovers
    : potentialAttachmentCovers.slice(0, 6);

  return (
    <>
      <h4 className={styles.heading}>{format('attachments')}</h4>
      {potentialAttachmentCovers.length > 0 && (
        <div className={styles.attachmentTiles}>
          {potentialAttachmentCoversToShow.map(
            ({ id, edgeColor, thumbnailUrl, selected }) => (
              <button
                key={id}
                className={cx('button', styles.attachmentTilesItem, {
                  [styles.selected]: selected,
                })}
                style={{
                  backgroundColor: edgeColor,
                  backgroundImage: `url('${thumbnailUrl}')`,
                  backgroundSize: 'contain',
                }}
                onClick={() => {
                  if (selected) {
                    setCover(null);
                    track(
                      'Card Cover Chooser',
                      'Toggled Cover Off',
                      'Attachment',
                    );
                  } else {
                    setCover({
                      idAttachment: id,
                    });
                    track('Card Cover Chooser', 'Chose Cover', 'Attachment');
                  }
                }}
              />
            ),
          )}
        </div>
      )}
      {potentialAttachmentCovers.length > 6 && !showMore && (
        <button
          className={cx('button', styles.showMoreButton)}
          onClick={() => setShowMore(true)}
        >
          {format('show-more')}
        </button>
      )}
      {showMore && (
        <button
          className={cx('button', styles.showFewerButton)}
          onClick={() => setShowMore(false)}
        >
          {format('show-fewer')}
        </button>
      )}
      <Upload cardId={cardId} className={styles.uploadButton} hideHeading />
    </>
  );
};
